/**
 * `Acceptance chase` — the missing-acceptance register at
 * `/countries/:cc/signoffs/acceptance-chase` (Train F, fork 21).
 *
 * ===========================================================================
 * WHAT ONE ROW IS, AND WHY THE LIST EXISTS
 * ===========================================================================
 * A PROJECT sign-off that is SIGNED and says nothing about the customer's
 * written acceptance — no document attached, no waiver stated. The record is
 * closed; the document that ends a dispute is not on file. These are the back
 * catalogue closed before the acceptance requirement existed (owner ruling R2),
 * and until this screen their only surface was a per-row amber label on the
 * work order's sign-off panel — a list nobody could read as a list.
 *
 * NOT the sign-off queue and NOT reachable from it as a filter: the queue lists
 * OUTSTANDING signatures and its WHERE excludes a signed row structurally, so
 * the two registers are disjoint by construction (registers.controller.ts:135-147).
 * The framing line under the header says that boundary out loud.
 *
 * ===========================================================================
 * THIS IS A CHASE LIST. IT RECORDS NOTHING.
 * ===========================================================================
 * The route is gated on `crf.view`, not on `signoff.*` — the controller says
 * why: chasing a document is reading, and the Coordinator who chases holds no
 * signing key. So this screen draws NO record-acceptance, waive, remind or send
 * control. Not disabled ones — none. The row's one action is a link to the CRF
 * record, where the acceptance is actually recorded behind its own keys.
 *
 * ===========================================================================
 * AN EMPTY CHASE LIST IS GOOD NEWS, AND THE SCREEN SAYS SO
 * ===========================================================================
 * Every other register's empty state is neutral ("no rows match"). This one is
 * not neutral: a signed project sign-off with no acceptance is a GAP, so zero
 * rows means every acceptance is on record or waived — the state the chase
 * exists to reach. The empty line is worded as the good news it is, and the
 * error state is worded so a read failure can NEVER be mistaken for it (a
 * danger Alert that says in words this is a read failure, not an empty list).
 *
 * ===========================================================================
 * NO OVERDUE TINT ON THE AGE, AND THAT IS A REFUSAL
 * ===========================================================================
 * The `SignoffQueuePage` refusal, inherited whole: nothing in the spec, the
 * API or the design names a number of days after which a missing acceptance is
 * "late". Painting an age red would be this screen inventing a claim the
 * server never made. The days render plain and the ordering — oldest signature
 * first, the server's — carries the urgency instead.
 *
 * `days_since_signed` is NULLABLE — null exactly when `signed_at` is, which
 * only a raw legacy insert produces. Null renders as a sentence saying the
 * signature date was never recorded; "0 days" would be a different and false
 * claim, that the signature happened today. Those rows sort to the BOTTOM
 * (`signed_at IS NULL ASC` leads the ORDER BY), so a gap nobody can age cannot
 * claim the top of an oldest-first list.
 *
 * ===========================================================================
 * TWO WIRE FIELDS ARE CARRIED AND NOT DRAWN, AND THAT IS A DECISION
 * ===========================================================================
 *   · `crf_status` — the CRF's own status ('signed_off' for a `sign()` close,
 *     but reported, not assumed). The unit brief's column list does not include
 *     it, and every row here is by definition a CLOSED record; a status column
 *     that reads "signed_off" on nearly every row would be noise dressed as
 *     signal. If a raw-insert CRF elsewhere ever matters here, this comment is
 *     where the reader learns the column was omitted, not forgotten.
 *   · `mail_status` — the legacy acknowledgement the signer recorded. The
 *     brief's columns are CRF / customer / signed by / days ago / action; the
 *     mail vocabulary already carries heavy caveats on the queue screen (it is
 *     an operator's note about mail that reached THEM — this system sends
 *     none), and importing that caveat load onto a screen the brief scoped
 *     without the column would be silent scope expansion. Flagged in the unit
 *     report for the owner rather than drawn.
 *
 * ===========================================================================
 * ONE MEASURED DIVERGENCE FROM `SignoffQueuePage`'S CELLS
 * ===========================================================================
 * Absence sentences and sub-lines here wear `text-fg-secondary`, not the
 * sibling's `text-fg-muted`. Not stylistic — measured, and the measurement is
 * already in the repository (`CrfRecordPage.tsx`, Manuals card header):
 *
 *     --crf-text-muted     #9aa0aa on --crf-bg-surface #ffffff  =  2.63:1  ✗ AA
 *     --crf-text-secondary #6e7580 on --crf-bg-surface #ffffff  =  4.64:1  ✓ AA
 *                          #9a9a9a on #1f1f1f (dark)            =  5.83:1  ✓ AA
 *
 * `text-fg-muted` is mode-invariant and fails AA on the light surface at
 * 12-13px, and the lines it would carry here — "No signature date recorded",
 * the deleted-account sentence — are exactly the text that must not be the
 * hardest to read. The sibling registers' cells are a pre-existing filed
 * defect (same header), not a pattern to extend; the table HEADER row keeps
 * the shared register chrome, whose re-toning is the owner's design decision.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateLong } from '../inquiries/format';
import { WorkTypeBadge } from '../crfs/WorkTypeBadge';
import { deploymentKeys, fetchAcceptanceChase, registerErrorText } from './deployment-api';
import type { AcceptanceChaseFilters, AcceptanceChaseRow } from './deployment-api';

const PER_PAGE = 50;

export function AcceptanceChasePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');

  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<AcceptanceChaseFilters>(() => ({ page, perPage: PER_PAGE }), [page]);

  const list = useQuery({
    queryKey: deploymentKeys.acceptanceChase(countryCode, filters),
    queryFn: ({ signal }) => fetchAcceptanceChase(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    // D5 — a designed refusal that names who to ask and never the permission key.
    // Never an empty table: on THIS screen an empty table reads as "every
    // acceptance is on record", the most expensive wrong answer it can give.
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The acceptance chase is not available to this account">
          Viewing the acceptance chase list in {countryCode} needs the CRF permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Acceptance chase"
        subtitle={
          list.data === undefined
            ? undefined
            : // The SERVER'S total, never the length of this page. One row is
              // exactly one missing acceptance, so the unit is acceptances.
              `${String(list.data.total)} ${
                list.data.total === 1 ? 'acceptance to chase' : 'acceptances to chase'
              }`
        }
      />

      {/*
       * The screen's own boundary, said out loud rather than left to be inferred
       * from the absence of buttons.
       */}
      <p className="text-body13 text-fg-secondary">
        Signed project sign-offs with no customer acceptance on record — no document attached
        and no waiver stated. This is not the sign-off queue: everything here is already
        signed, and what is being chased is the customer&apos;s written acceptance. Nothing is
        recorded from here: open the CRF to file the acceptance or its waiver.
      </p>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the acceptance chase list" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The acceptance chase list could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary. And the
              boundary this screen cannot leave implicit: on a GOOD-NEWS register a
              silent failure would read as the good news itself. */}
          <p>
            {registerErrorText(list.error)} This is a read failure, not an empty chase list —
            nothing here says the acceptances are on record.
          </p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes-list V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)}{' '}
            {total === 1 ? 'missing acceptance' : 'missing acceptances'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        /*
         * NOTHING TO CHASE IS GOOD NEWS, and the words say so. This empty state
         * carries no permission-aware clause deliberately: the clause exists so a
         * reader who cannot act learns why a control is missing, and on this
         * screen the control is missing for EVERYONE — a chase list records
         * nothing, whoever is reading it.
         */
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="Nothing to chase — every signed project sign-off in this country has its acceptance on record or a stated waiver."
          />
        </div>
      ) : (
        <AcceptanceChaseTable countryCode={countryCode} rows={rows} />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

function AcceptanceChaseTable({
  countryCode,
  rows,
}: {
  countryCode: string;
  rows: readonly AcceptanceChaseRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[980px] border-collapse text-left">
        <caption className="sr-only">
          Every signed project sign-off in {countryCode} with no customer acceptance on record,
          oldest signature first
        </caption>
        <colgroup>
          <col className="w-[170px]" />
          <col />
          <col className="w-[220px]" />
          <col className="w-[200px]" />
          <col className="w-[110px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              CRF
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              CUSTOMER
            </th>
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              SIGNED BY
            </th>
            {/*
             * Server-ordered by `signed_at IS NULL ASC, signed_at ASC` — stated,
             * not a dead sort control. The leading IS NULL key keeps a row with no
             * signature date at the BOTTOM; without it MySQL would sort NULLs
             * first and the top of an oldest-first chase list would be the row
             * carrying no age at all — the queue screen's own recorded lesson.
             * The ↓ glyph is aria-hidden, so the sr-only sentence is the ONLY
             * ordering information a screen-reader user gets; it must keep saying
             * what the server actually does.
             */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              SIGNED <span aria-hidden="true">↓</span>
              <span className="sr-only">
                , oldest signature first; rows with no recorded signature date come last
              </span>
            </th>
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={row.crf_id}
              className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle"
            >
              <td className="px-3 py-3 align-middle">
                <span className="flex items-center gap-2">
                  <Link
                    to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                    className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                  >
                    {row.crf_doc_no}
                  </Link>
                  <WorkTypeBadge workType={row.work_type} />
                </span>
              </td>

              <td className="px-3 py-3 align-middle text-body13 text-fg">{row.party_name}</td>

              <SignedByCell by={row.signed_by} />

              <SignedAgeCell days={row.days_since_signed} at={row.signed_at} />

              <td className="px-3 py-3 align-middle">
                {/*
                 * The row's ONE action, and it is navigation — to the CRF record,
                 * where the acceptance is actually recorded. This is the cell a
                 * "Record acceptance" button would have occupied; it holds a link
                 * instead, and that is the whole difference between a chase list
                 * and a recording surface.
                 */}
                <Link
                  to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                  className="whitespace-nowrap text-body13 font-medium text-indigo hover:underline"
                >
                  Open CRF
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Who closed the record — the person to ask first about the missing document.
 *
 * The deleted-account sentence is `SignoffQueuePage`'s own wording, kept so the
 * same fact reads the same in both registers, and the cell borrows no other
 * row's name in that case.
 */
function SignedByCell({ by }: { by: string | null }): React.JSX.Element {
  if (by === null) {
    return (
      <td className="px-3 py-3 align-middle">
        <span
          className="text-label text-fg-secondary"
          title="The account that signed no longer exists, so it cannot be named."
        >
          Signed by an account that no longer exists
        </span>
      </td>
    );
  }
  return <td className="px-3 py-3 align-middle text-body13 text-fg">{by}</td>;
}

/**
 * The age of the gap — how many days ago the project signature closed a record
 * whose acceptance never arrived.
 *
 * `days_since_signed` is the SERVER's arithmetic, rendered rather than
 * recomputed, so every reader of this row agrees on the number. Null means the
 * signature date was never recorded (raw legacy insert) and is said in words —
 * "0 days ago" would claim the signature happened today. No overdue tint: see
 * the file header.
 */
function SignedAgeCell({ days, at }: { days: number | null; at: string | null }): React.JSX.Element {
  if (days === null) {
    return (
      <td className="px-3 py-3 align-middle">
        <span
          data-signed="no-date"
          title="This sign-off row carries no signature timestamp — only a raw legacy insert produces that shape — so the age of the gap cannot be measured."
          className="text-label text-fg-secondary"
        >
          No signature date recorded
        </span>
      </td>
    );
  }
  return (
    <td className="px-3 py-3 align-middle">
      <div className="flex flex-col gap-0.5">
        <span className="tnum text-body13 font-medium text-fg">
          {String(days)} {days === 1 ? 'day' : 'days'} ago
        </span>
        {at === null ? null : (
          <span className="text-label text-fg-secondary">signed {formatDateLong(at)}</span>
        )}
      </div>
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/**
 * `Table/Pagination` — re-authored per screen, the house pattern every list in
 * this app follows: the geometry is shared, the component is file-private.
 */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
